import React from 'react';
import { SheetLabel, SheetNote, UtSheet } from './UtSheet';
import { decayRows, recoveryGames, streakBonusPct } from './sheetMaths';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  busy: boolean;
  matchNumber?: number | string;
  tokensAvailable: number;
  /**
   * Streak the shield will protect. use_shield_token protects the natural
   * streak, or the effective one when a shield is already easing down.
   */
  protectedStreak: number;
  /** A shield from an earlier week is still running (protecting or easing down). */
  alreadyEasing: boolean;
  /** Registered for this game: use_shield_token deletes the registration. */
  isRegistered: boolean;
}

/** "Use a shield" sheet: what's protected, how the bonus rebuilds, and the rules. Action is the caller's (useShieldToken). */
export const UtShieldSheet: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  busy,
  matchNumber,
  tokensAvailable,
  protectedStreak,
  alreadyEasing,
  isRegistered,
}) => {
  const streak = Math.max(0, protectedStreak);
  const meet = recoveryGames(streak);
  const rows = decayRows(streak);
  const eyebrow = [matchNumber ? `Match #${matchNumber}` : null, `${tokensAvailable} ${tokensAvailable === 1 ? 'shield' : 'shields'} held`]
    .filter(Boolean)
    .join(' · ');
  const left = Math.max(0, tokensAvailable - 1);

  return (
    <UtSheet
      open={open}
      onClose={onClose}
      busy={busy}
      eyebrow={eyebrow}
      eyebrowClassName="text-ut-sky"
      title="Use a shield"
      main={{
        label: busy ? 'WORKING…' : `USE SHIELD · ${left} LEFT`,
        onClick: onConfirm,
        disabled: tokensAvailable <= 0,
        className: 'bg-ut-sky text-[#06121f]',
      }}
    >
      <div className="grid grid-cols-3 gap-2">
        <Stat value={streak} label="Streak protected" className="text-ut-sky" />
        <Stat value={`+${streakBonusPct(streak)}%`} label="XP bonus now" className="text-ut-acc" />
        <Stat value={meet} label="Games to recover" className="text-ut-ok" />
      </div>

      {streak > 0 ? (
        <div className="flex flex-col gap-2.5 rounded-xl bg-ut-card p-3.5">
          <SheetLabel>How you rebuild when you&apos;re back</SheetLabel>
          {rows.map((r) => (
            <div key={r.game} className="grid grid-cols-[56px_1fr_52px] items-center gap-2.5 text-[13px]">
              <span className="text-ut-mut">Game {r.game}</span>
              <div className="flex h-3.5 overflow-hidden rounded bg-ut-field">
                <span className="bg-ut-ok" style={{ width: `${(Math.min(r.natural, streak) / streak) * 100}%` }} />
                <span className="bg-ut-sky/70" style={{ width: `${(r.lift / streak) * 100}%` }} />
              </div>
              <b className={r.game >= meet ? 'text-right text-ut-ok' : 'text-right'}>+{r.pct}%</b>
            </div>
          ))}
          <div className="flex gap-3.5 text-xs text-ut-mut">
            <span className="flex items-center gap-[5px]">
              <span className="h-2.5 w-2.5 rounded-sm bg-ut-ok" />
              Natural streak
            </span>
            <span className="flex items-center gap-[5px]">
              <span className="h-2.5 w-2.5 rounded-sm bg-ut-sky/70" />
              Shield protection
            </span>
          </div>
          <span className="text-[13px] leading-[1.45]">
            After <b>{meet} {meet === 1 ? 'game' : 'games'}</b> back, the two meet and the shield drops off. You keep building from there.
          </span>
        </div>
      ) : (
        <div className="rounded-xl bg-ut-card px-3.5 py-3 text-[13px] leading-[1.45] text-ut-mut">
          You don&apos;t have a streak to protect yet, so a shield gains you nothing this week.
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        {isRegistered && (
          <SheetNote k="Note" tone="acc">
            You&apos;re registered for this game. Using a shield cancels your registration.
          </SheetNote>
        )}
        {alreadyEasing && (
          <SheetNote k="Note" tone="acc">
            You already have a shield running. This one protects your current {streak}-game streak.
          </SheetNote>
        )}
        <SheetNote k="Rule" tone="bad">
          Miss a game during recovery without another shield and your streak resets to 0.
        </SheetNote>
        <SheetNote k="Longer break?">Each missed week needs its own shield. Recovery only starts when you play again.</SheetNote>
        <span className="text-xs text-ut-mut">You can cancel the shield until registration closes.</span>
      </div>
    </UtSheet>
  );
};

const Stat: React.FC<{ value: React.ReactNode; label: string; className: string }> = ({ value, label, className }) => (
  <div className="flex flex-col gap-0.5 rounded-xl bg-ut-card p-3">
    <span className={`font-ut-display text-[30px] font-extrabold leading-none ${className}`}>{value}</span>
    <span className="text-xs text-ut-mut">{label}</span>
  </div>
);
